import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Card from '../common/Card';
import './ShipDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ShipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ship, setShip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('info');

  const getToken = () => localStorage.getItem('marilink_token');

  useEffect(() => {
    const fetchShip = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/vessels/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.success) {
          setShip(data.vessel);
        } else {
          setError(data.message || 'Vessel not found');
        }
      } catch (err) {
        console.error('Fetch vessel error:', err);
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchShip();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove this vessel? This action cannot be undone.')) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/vessels/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        alert('Vessel deleted successfully');
        navigate('/owner/my-ships');
      } else {
        alert(data.message || 'Delete failed');
      }
    } catch (err) {
      alert('Error deleting vessel: ' + err.message);
    }
  };

  if (loading) return <div className="loading-spinner">Loading vessel details...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!ship) return <div className="error-message">Vessel not found</div>;

  return (
    <div className="ship-detail-page">
      <button className="back-btn" onClick={() => navigate('/owner/my-ships')}>
        <i className="fas fa-arrow-left"></i> Back to My Ships
      </button>

      <div className="ship-header-card">
        <div className="ship-header">
          <div className="ship-title">
            <h1>{ship.name}</h1>
          </div>
          <div className="ship-meta">
            <span className="imo-badge">IMO: {ship.imo}</span>
            <span className={`status-badge ${ship.status}`}>
              {ship.status === 'active' ? 'Active' : ship.status === 'maintenance' ? 'Maintenance' : 'Inactive'}
            </span>
          </div>
        </div>
        <p className="ship-description">{ship.type} • {ship.flag} Flag • Built {ship.built || 'N/A'}</p>
      </div>

      <div className="ship-stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-weight-hanging"></i></div>
          <div>
            <div className="stat-value">{Number(ship.grt).toLocaleString()} GT</div>
            <div className="stat-label">Gross Tonnage</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
          <div>
            <div className="stat-value">{Number(ship.dwt || 0).toLocaleString()} t</div>
            <div className="stat-label">Deadweight</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-heartbeat"></i></div>
          <div>
            <div className="stat-value">{ship.health || 100}%</div>
            <div className="stat-label">Health Score</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-calendar-alt"></i></div>
          <div>
            <div className="stat-value">{ship.built || 'N/A'}</div>
            <div className="stat-label">Year Built</div>
          </div>
        </div>
      </div>

      <div className="location-card">
        <div className="location-row">
          <div className="port-info">
            <i className="fas fa-anchor"></i>
            <span>Last Port: <strong>{ship.lastPort || 'N/A'}</strong></span>
          </div>
          <i className="fas fa-arrow-right"></i>
          <div className="port-info">
            <i className="fas fa-map-marker-alt"></i>
            <span>Next Port: <strong>{ship.nextPort || 'N/A'}</strong></span>
          </div>
          <div className="eta-info">
            <i className="far fa-clock"></i>
            <span>ETA: <strong>{ship.eta ? new Date(ship.eta).toLocaleDateString() : 'N/A'}</strong></span>
          </div>
        </div>
      </div>

      <div className="ship-tabs">
        <button className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          <i className="fas fa-info-circle"></i> Information
        </button>
        <button className={`tab-btn ${activeTab === 'certificates' ? 'active' : ''}`} onClick={() => setActiveTab('certificates')}>
          <i className="fas fa-certificate"></i> Certificates
        </button>
        <button className={`tab-btn ${activeTab === 'maintenance' ? 'active' : ''}`} onClick={() => setActiveTab('maintenance')}>
          <i className="fas fa-tools"></i> Maintenance Log
        </button>
        <button className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}>
          <i className="fas fa-briefcase"></i> Job History
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'info' && (
          <Card title="Vessel Specifications">
            <div className="info-grid">
              <div><span className="label">Vessel Name</span><span className="value">{ship.name}</span></div>
              <div><span className="label">IMO Number</span><span className="value">{ship.imo}</span></div>
              <div><span className="label">Vessel Type</span><span className="value">{ship.type}</span></div>
              <div><span className="label">Flag State</span><span className="value">{ship.flag}</span></div>
              <div><span className="label">Gross Tonnage</span><span className="value">{Number(ship.grt).toLocaleString()} GT</span></div>
              <div><span className="label">Deadweight</span><span className="value">{Number(ship.dwt || 0).toLocaleString()} t</span></div>
              <div><span className="label">Year Built</span><span className="value">{ship.built || 'N/A'}</span></div>
              <div><span className="label">Status</span><span className="value">{ship.status}</span></div>
            </div>
          </Card>
        )}

        {activeTab === 'certificates' && (
          <div className="certificates-tab">
            <p className="info-message">Certificate management coming soon. Please use the Documents page.</p>
            <Button variant="secondary" onClick={() => navigate('/owner/certificates')}>
              Go to Documents
            </Button>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="maintenance-tab">
            <p className="info-message">To view maintenance logs, please check your completed jobs.</p>
            <Button variant="secondary" onClick={() => navigate('/owner/my-jobs-bids')}>
              View Job History
            </Button>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="jobs-tab">
            <p className="info-message">Active and past jobs for this vessel.</p>
            <Button variant="secondary" onClick={() => navigate('/owner/post-job')}>
              Post a Job for This Vessel
            </Button>
          </div>
        )}
      </div>

      <div className="action-buttons">
        <Button variant="primary" icon="fa-edit" onClick={() => alert('Edit feature coming soon')}>
          Edit Vessel
        </Button>
        <Button variant="secondary" icon="fa-plus" onClick={() => navigate('/owner/post-job')}>
          Post Job
        </Button>
        <div className="right-actions">
          <Button variant="danger" icon="fa-trash" onClick={handleDelete}>
            Remove Vessel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShipDetail;